import { useState, useMemo } from 'react';
import {
  researchQuestions,
  researchCategories,
  demographicDimensions,
  type ResearchQuestion,
  type DemographicCut,
} from '@/data/researchData';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

// ============================================================================
// TYPES
// ============================================================================

interface Brand {
  name: string;
  share: number;
  color?: string;
}

interface CrossTabsViewProps {
  brands: Brand[];
  language: string;
  onExport?: () => void;
}

// ============================================================================
// BRAND-BASED CUTS (simulated)
// ============================================================================

// Simulate brand-based response distributions
const generateBrandCuts = (brands: Brand[], basePct: number): { label: string; value: number }[] => {
  return brands.map((brand, i) => ({
    label: brand.name,
    value: Math.max(5, basePct + (Math.random() * 20 - 10) * (1 - brand.share)), // Vary based on share
  }));
};

// ============================================================================
// LABELS
// ============================================================================

const LABELS = {
  en: {
    title: 'Survey Cross-Tabs',
    subtitle: 'Analyze survey responses by demographics and brands',
    category: 'Category',
    question: 'Question',
    dimension: 'Demographic Cut',
    brandCut: 'By Brand',
    allCategories: 'All Categories',
    overallResults: 'Overall Results',
    byDimension: 'By',
    byBrand: 'By Brand',
    detailedResults: 'Detailed Results',
    response: 'Response',
    total: 'Total',
    exportExcel: 'Export to Excel',
    sourceTypes: 'Source Types',
    human: 'Human',
    synthetic: 'Synthetic',
    hybrid: 'Hybrid',
    humanDesc: 'Traditional surveys with real people',
    syntheticDesc: 'AI-generated based on consumer models',
    hybridDesc: 'Combination of human and synthetic data',
    type: 'Type',
    singleChoice: 'Single Choice',
    multipleChoice: 'Multiple Choice',
    tier1Info: 'TIER 1 Research Questions',
  },
  es: {
    title: 'Cross-Tabs de Encuesta',
    subtitle: 'Analiza respuestas de encuestas por demografía y marcas',
    category: 'Categoría',
    question: 'Pregunta',
    dimension: 'Corte Demográfico',
    brandCut: 'Por Marca',
    allCategories: 'Todas las Categorías',
    overallResults: 'Resultados Generales',
    byDimension: 'Por',
    byBrand: 'Por Marca',
    detailedResults: 'Resultados Detallados',
    response: 'Respuesta',
    total: 'Total',
    exportExcel: 'Exportar a Excel',
    sourceTypes: 'Tipos de Fuente',
    human: 'Humano',
    synthetic: 'Sintético',
    hybrid: 'Híbrido',
    humanDesc: 'Encuestas tradicionales con personas reales',
    syntheticDesc: 'Generado por IA basado en modelos de consumidor',
    hybridDesc: 'Combinación de datos humanos y sintéticos',
    type: 'Tipo',
    singleChoice: 'Opción Única',
    multipleChoice: 'Opción Múltiple',
    tier1Info: 'Preguntas de Investigación TIER 1',
  },
};

// Source type styles
const sourceTypeConfig = {
  synthetic: { bg: 'bg-purple-500/20', text: 'text-purple-400' },
  human: { bg: 'bg-green-500/20', text: 'text-green-400' },
  hybrid: { bg: 'bg-amber-500/20', text: 'text-amber-400' },
};

const COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

// ============================================================================
// COMPONENT
// ============================================================================

export function CrossTabsView({ brands, language, onExport }: CrossTabsViewProps) {
  const t = language === 'es' ? LABELS.es : LABELS.en;

  // State
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedQuestion, setSelectedQuestion] = useState<ResearchQuestion | null>(
    researchQuestions[0] || null
  );
  const [selectedDimension, setSelectedDimension] = useState<string>('by_age');
  const [showBrandCut, setShowBrandCut] = useState<boolean>(true);

  // Filter questions by category
  const filteredQuestions = useMemo(() =>
    selectedCategory === 'all'
      ? researchQuestions
      : researchQuestions.filter((q) => q.category === selectedCategory),
    [selectedCategory]
  );

  // Get demographic dimension data
  const getDimensionData = (question: ResearchQuestion, dimension: string): DemographicCut[] => {
    if (!question.answers[0]) return [];
    const firstAnswer = question.answers[0];
    return (firstAnswer as unknown as Record<string, DemographicCut[]>)[dimension] || [];
  };

  // Generate brand cut data for the selected question
  const brandCutData = useMemo(() => {
    if (!selectedQuestion || brands.length === 0) return null;

    return selectedQuestion.answers.map((answer) => ({
      answerLabel: answer.label,
      cuts: generateBrandCuts(brands, answer.total_pct),
    }));
  }, [selectedQuestion, brands]);

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle export
  const handleExport = () => {
    if (!selectedQuestion) return;

    let csv = 'Survey Cross-Tabs Export\n';
    csv += `Question: ${selectedQuestion.question}\n`;
    csv += `Date: ${formatDate(selectedQuestion.dateAsked)}\n`;
    csv += `Sample Size: n=${selectedQuestion.sampleSize}\n\n`;

    // Overall Results
    csv += 'Overall Results\n';
    csv += 'Response,Percentage\n';
    selectedQuestion.answers.forEach((a) => {
      csv += `${a.label},${a.total_pct.toFixed(1)}%\n`;
    });
    csv += '\n';

    // Demographic Cut
    const dimLabel = demographicDimensions.find((d) => d.id === selectedDimension)?.label || '';
    const dimData = getDimensionData(selectedQuestion, selectedDimension);
    csv += `By ${dimLabel}\n`;
    csv += `Response,${dimData.map((d) => d.label).join(',')}\n`;
    selectedQuestion.answers.forEach((answer) => {
      const answerCuts = (answer as unknown as Record<string, DemographicCut[]>)[selectedDimension];
      csv += `${answer.label},${dimData.map((cut) => {
        const match = answerCuts?.find((c) => c.label === cut.label);
        return (match?.value || 0).toFixed(1) + '%';
      }).join(',')}\n`;
    });
    csv += '\n';

    // Brand Cut
    if (brandCutData) {
      csv += 'By Brand\n';
      csv += `Response,${brands.map((b) => b.name).join(',')}\n`;
      brandCutData.forEach((row) => {
        csv += `${row.answerLabel},${row.cuts.map((c) => c.value.toFixed(1) + '%').join(',')}\n`;
      });
    }

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `cross-tabs-${selectedQuestion.id}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();

    onExport?.();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">{t.title}</h2>
          <p className="text-text-secondary text-sm mt-1">{t.subtitle}</p>
        </div>
        <button
          onClick={handleExport}
          disabled={!selectedQuestion}
          className="px-4 py-2 text-sm bg-surface-700 hover:bg-surface-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          {t.exportExcel}
        </button>
      </div>

      {/* Filters Row */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t.category}
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                const firstQ =
                  e.target.value === 'all'
                    ? researchQuestions[0]
                    : researchQuestions.find((q) => q.category === e.target.value);
                setSelectedQuestion(firstQ || null);
              }}
              className="w-full bg-surface-700 border border-surface-500 rounded-lg px-3 py-2.5 text-base text-white focus:outline-none focus:border-primary-500"
            >
              <option value="all">{t.allCategories}</option>
              {researchCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Question Selector */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t.question}
            </label>
            <select
              value={selectedQuestion?.id || ''}
              onChange={(e) => {
                const q = researchQuestions.find((q) => q.id === e.target.value);
                setSelectedQuestion(q || null);
              }}
              className="w-full bg-surface-700 border border-surface-500 rounded-lg px-3 py-2.5 text-base text-white focus:outline-none focus:border-primary-500"
            >
              {filteredQuestions.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.id}: {q.question.substring(0, 35)}...
                </option>
              ))}
            </select>
          </div>

          {/* Demographic Dimension */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t.dimension}
            </label>
            <select
              value={selectedDimension}
              onChange={(e) => setSelectedDimension(e.target.value)}
              className="w-full bg-surface-700 border border-surface-500 rounded-lg px-3 py-2.5 text-base text-white focus:outline-none focus:border-primary-500"
            >
              {demographicDimensions.map((dim) => (
                <option key={dim.id} value={dim.id}>
                  {dim.label}
                </option>
              ))}
            </select>
          </div>

          {/* Brand Cut Toggle */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t.brandCut}
            </label>
            <label className="flex items-center gap-3 cursor-pointer h-[42px] px-3 bg-surface-700 border border-surface-500 rounded-lg">
              <input
                type="checkbox"
                checked={showBrandCut}
                onChange={(e) => setShowBrandCut(e.target.checked)}
                className="w-4 h-4 rounded bg-surface-600 border-surface-500 text-primary-500 focus:ring-primary-500 focus:ring-offset-0"
              />
              <span className="text-white text-sm">
                {language === 'es' ? 'Mostrar corte por marca' : 'Show brand breakdown'}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Question Detail */}
      {selectedQuestion && (
        <>
          {/* Question Header */}
          <div className="card p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary-500/10 rounded-lg">
                <span className="text-2xl">📊</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center flex-wrap gap-2 mb-2">
                  <span className="text-xs font-medium text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded">
                    {selectedQuestion.id}
                  </span>
                  <span className="text-xs text-text-tertiary">
                    {selectedQuestion.section}
                  </span>
                  {/* Source Type Badge */}
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${sourceTypeConfig[selectedQuestion.sourceType].bg} ${sourceTypeConfig[selectedQuestion.sourceType].text}`}>
                    {language === 'es'
                      ? (selectedQuestion.sourceType === 'human' ? 'Humano' : selectedQuestion.sourceType === 'synthetic' ? 'Sintético' : 'Híbrido')
                      : (selectedQuestion.sourceType.charAt(0).toUpperCase() + selectedQuestion.sourceType.slice(1))}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-white">
                  {selectedQuestion.question}
                </h3>
                {/* Metadata Row */}
                <div className="flex items-center flex-wrap gap-4 mt-2 text-sm text-text-secondary">
                  <span>
                    {t.type}: {selectedQuestion.questionType === 'single' ? t.singleChoice : t.multipleChoice}
                  </span>
                  <span className="text-text-tertiary">|</span>
                  <span>
                    📅 {formatDate(selectedQuestion.dateAsked)}
                  </span>
                  <span className="text-text-tertiary">|</span>
                  <span>
                    👥 n={selectedQuestion.sampleSize.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Overall Results Bar Chart */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">{t.overallResults}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={selectedQuestion.answers.map((a) => ({
                    name: a.label.length > 20 ? a.label.substring(0, 20) + '...' : a.label,
                    value: Math.round(a.total_pct * 10) / 10,
                  }))}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fill: '#9ca3af' }} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={150}
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [`${value}%`, language === 'es' ? 'Porcentaje' : 'Percentage']}
                  />
                  <Bar dataKey="value" fill="#06b6d4" radius={[0, 4, 4, 0]}>
                    {selectedQuestion.answers.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Demographic Breakdown */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                {t.byDimension} {demographicDimensions.find((d) => d.id === selectedDimension)?.label}
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={getDimensionData(selectedQuestion, selectedDimension).map((cut) => {
                    const dataPoint: Record<string, string | number> = { name: cut.label };
                    selectedQuestion.answers.forEach((answer, i) => {
                      const answerCuts = (answer as unknown as Record<string, DemographicCut[]>)[selectedDimension];
                      const matchingCut = answerCuts?.find((c) => c.label === cut.label);
                      dataPoint[`ans${i}`] = Math.round((matchingCut?.value || 0) * 10) / 10;
                    });
                    return dataPoint;
                  })}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: '#9ca3af' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value, name) => {
                      const idx = parseInt(String(name).replace('ans', ''));
                      const ansLabel = selectedQuestion.answers[idx]?.label || String(name);
                      return [`${value}%`, ansLabel.substring(0, 25)];
                    }}
                  />
                  <Legend
                    formatter={(value: string) => {
                      const idx = parseInt(value.replace('ans', ''));
                      const label = selectedQuestion.answers[idx]?.label || value;
                      return <span className="text-xs text-text-secondary">{label.length > 15 ? label.substring(0, 15) + '...' : label}</span>;
                    }}
                  />
                  {selectedQuestion.answers.map((_, i) => (
                    <Bar key={i} dataKey={`ans${i}`} fill={COLORS[i % COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Brand Cut Chart (if enabled) */}
          {showBrandCut && brandCutData && brands.length > 0 && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">{t.byBrand}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={brands.map((brand, bIdx) => {
                    const dataPoint: Record<string, string | number> = { name: brand.name };
                    brandCutData.forEach((row, aIdx) => {
                      const cut = row.cuts.find((c) => c.label === brand.name);
                      dataPoint[`ans${aIdx}`] = Math.round((cut?.value || 0) * 10) / 10;
                    });
                    return dataPoint;
                  })}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: '#9ca3af' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value, name) => {
                      const idx = parseInt(String(name).replace('ans', ''));
                      const ansLabel = selectedQuestion.answers[idx]?.label || String(name);
                      return [`${value}%`, ansLabel.substring(0, 25)];
                    }}
                  />
                  <Legend
                    formatter={(value: string) => {
                      const idx = parseInt(value.replace('ans', ''));
                      const label = selectedQuestion.answers[idx]?.label || value;
                      return <span className="text-xs text-text-secondary">{label.length > 15 ? label.substring(0, 15) + '...' : label}</span>;
                    }}
                  />
                  {selectedQuestion.answers.map((_, i) => (
                    <Bar key={i} dataKey={`ans${i}`} fill={COLORS[i % COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Detailed Results Table */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">{t.detailedResults}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-500">
                    <th className="text-left py-3 px-4 text-text-secondary font-medium">
                      {t.response}
                    </th>
                    <th className="text-right py-3 px-4 text-text-secondary font-medium">{t.total} %</th>
                    {getDimensionData(selectedQuestion, selectedDimension).map((cut) => (
                      <th key={cut.label} className="text-right py-3 px-4 text-text-secondary font-medium">
                        {cut.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedQuestion.answers.map((answer, i) => (
                    <tr key={i} className="border-b border-surface-600 hover:bg-surface-700/50">
                      <td className="py-3 px-4 text-white">{answer.label}</td>
                      <td className="text-right py-3 px-4 text-primary-400 font-medium">
                        {answer.total_pct.toFixed(1)}%
                      </td>
                      {getDimensionData(selectedQuestion, selectedDimension).map((cut) => {
                        const answerCuts = (answer as unknown as Record<string, DemographicCut[]>)[selectedDimension];
                        const matchingCut = answerCuts?.find((c) => c.label === cut.label);
                        return (
                          <td key={cut.label} className="text-right py-3 px-4 text-text-secondary">
                            {(matchingCut?.value || 0).toFixed(1)}%
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Source Type Legend */}
          <div className="card p-4">
            <h4 className="text-sm font-medium text-white mb-3">{t.sourceTypes}</h4>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${sourceTypeConfig.human.bg} ${sourceTypeConfig.human.text}`}>
                  {t.human}
                </span>
                <span className="text-xs text-text-secondary">{t.humanDesc}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${sourceTypeConfig.synthetic.bg} ${sourceTypeConfig.synthetic.text}`}>
                  {t.synthetic}
                </span>
                <span className="text-xs text-text-secondary">{t.syntheticDesc}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${sourceTypeConfig.hybrid.bg} ${sourceTypeConfig.hybrid.text}`}>
                  {t.hybrid}
                </span>
                <span className="text-xs text-text-secondary">{t.hybridDesc}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
